/**
 * Red Dog (Acey-Deucey, In-Between) — server-authoritative rules engine (2–6 players, session game with chips).
 *
 * Rules implemented:
 *  - One 52-card deck, reshuffled before every round. The ace is high only; suits never matter.
 *  - Every player antes (or sits the round out). Two cards are dealt face up to make the spread, shared by everyone.
 *  - Consecutive ranks (no rank between them) are a push: the ante comes back.
 *  - Equal ranks: a third card is drawn at once. Three of a kind pays 11:1, anything else is a push.
 *  - Otherwise the spread is the number of ranks strictly between the two cards. Each player may raise their bet by up
 *    to the size of their ante, or stay. One shared third card is then drawn: strictly between the two cards wins,
 *    anything else loses. A win pays 5:1 on a spread of 1, 4:1 on 2, 2:1 on 3 and 1:1 on 4 or more, on ante and raise.
 */
import { buildDeck, parseCard, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type SeatSetup,
} from '../../core/types.js';

export interface RedDogConfig {
  minBet: number;
  maxBet: number;
  minPlayers: number;
  maxPlayers: number;
  /** Three of a kind on an equal-rank spread pays this many times the ante. */
  tripsPays: number;
  startDelayMs: number;
  dealDelayMs: number;
  handEndDelayMs: number;
}

export type RedDogPhase = 'WAITING' | 'BETTING' | 'DEALING' | 'DECIDING' | 'DRAWING' | 'HAND_END';
export type RedDogSeatStatus = 'WAITING' | 'BET' | 'SIT_OUT';
export type RedDogOutcome = 'WIN' | 'LOSE' | 'PUSH' | 'TRIPS';
/** How the two spread cards relate: touching, equal, or with ranks between them. */
export type RedDogSpreadKind = 'CONSECUTIVE' | 'PAIR' | 'SPREAD';

export interface RedDogSeat {
  playerId: string;
  chips: number;
  status: RedDogSeatStatus;
  bet: number;
  raise: number;
  /** Set once the player has raised or stayed on a live spread. */
  decided: boolean;
  outcome: RedDogOutcome | null;
  /** What came back at the end of the round (stake plus winnings, or 0). */
  payout: number;
}

export interface RedDogResult {
  handNo: number;
  spread: [CardId, CardId];
  third: CardId | null;
  kind: RedDogSpreadKind;
  seats: { seat: number; bet: number; raise: number; outcome: RedDogOutcome; payout: number; delta: number }[];
}

export interface RedDogState {
  config: RedDogConfig;
  seats: (RedDogSeat | null)[];
  phase: RedDogPhase;
  handNo: number;
  /** Seat to bet, or to raise or stay. */
  turn: number;
  deck: CardId[];
  spread: [CardId, CardId] | null;
  third: CardId | null;
  lastResult: RedDogResult | null;
  handsPlayed: number;
}

export type RedDogAction = { type: 'bet'; amount: number } | { type: 'sitOut' } | { type: 'raise'; amount: number } | { type: 'stay' };

export type RedDogLegal =
  | { kind: 'bet'; min: number; max: number; canSitOut: boolean }
  | { kind: 'decide'; maxRaise: number; spread: number }
  | { kind: 'none' };

export interface RedDogView {
  gameType: 'RED_DOG';
  config: RedDogConfig;
  seat: number | null;
  phase: RedDogPhase;
  handNo: number;
  turn: number;
  spread: [CardId, CardId] | null;
  third: CardId | null;
  /** Ranks strictly between the spread cards, or null before the spread is dealt. */
  spreadSize: number | null;
  deckCount: number;
  players: ({ seat: number; playerId: string; chips: number; status: RedDogSeatStatus; bet: number; raise: number; decided: boolean; outcome: RedDogOutcome | null; payout: number } | null)[];
  lastResult: RedDogResult | null;
}

const DEFAULTS: RedDogConfig = {
  minBet: 10,
  maxBet: 1000,
  minPlayers: 2,
  maxPlayers: 6,
  tripsPays: 11,
  startDelayMs: 2000,
  dealDelayMs: 1100,
  handEndDelayMs: 4000,
};

/** Red Dog card value: 2 up to the ace at 14. */
export const redDogValue = (card: CardId): number => parseCard(card).rank;

/** Ranks strictly between two cards (-1 when the ranks are equal, 0 when they touch). */
export const redDogSpread = (a: CardId, b: CardId): number => Math.abs(redDogValue(a) - redDogValue(b)) - 1;

export const redDogKind = (a: CardId, b: CardId): RedDogSpreadKind => {
  const n = redDogSpread(a, b);
  return n < 0 ? 'PAIR' : n === 0 ? 'CONSECUTIVE' : 'SPREAD';
};

/** The win pays this many times the bet for a spread of `n` ranks. */
export const redDogOdds = (n: number): number => (n <= 1 ? 5 : n === 2 ? 4 : n === 3 ? 2 : 1);

/** Whether the third card lands strictly between the two spread cards. */
export const redDogBetween = (a: CardId, b: CardId, third: CardId): boolean => {
  const lo = Math.min(redDogValue(a), redDogValue(b));
  const hi = Math.max(redDogValue(a), redDogValue(b));
  const v = redDogValue(third);
  return v > lo && v < hi;
};

function seatsWhere(s: RedDogState, pred: (p: RedDogSeat, i: number) => boolean): number[] {
  const out: number[] = [];
  s.seats.forEach((p, i) => {
    if (p && pred(p, i)) out.push(i);
  });
  return out;
}

const canStart = (s: RedDogState): boolean => seatsWhere(s, (p) => p.chips >= s.config.minBet).length >= s.config.minPlayers;

function advanceBetting(s: RedDogState, from: number): void {
  for (let seat = from + 1; seat < s.seats.length; seat++) {
    const p = s.seats[seat];
    if (p && p.status === 'WAITING') {
      s.turn = seat;
      return;
    }
  }
  s.phase = seatsWhere(s, (p) => p.status === 'BET').length > 0 ? 'DEALING' : 'WAITING';
}

/** The next betting seat, after `from`, that still has to raise or stay. */
function nextDecider(s: RedDogState, from: number): number {
  for (let seat = from + 1; seat < s.seats.length; seat++) {
    const p = s.seats[seat];
    if (p && p.status === 'BET' && !p.decided) return seat;
  }
  return -1;
}

function startHand(s: RedDogState, rng: Rng, events: EngineEvent[]): void {
  s.handNo++;
  s.deck = shuffle(buildDeck(), rng);
  s.spread = null;
  s.third = null;
  for (const p of s.seats) {
    if (!p) continue;
    p.bet = 0;
    p.raise = 0;
    p.decided = false;
    p.outcome = null;
    p.payout = 0;
    p.status = p.chips >= s.config.minBet ? 'WAITING' : 'SIT_OUT';
  }
  s.phase = 'BETTING';
  s.turn = -1;
  events.push({ type: 'handStart', handNo: s.handNo });
  advanceBetting(s, -1);
  if (s.phase !== 'BETTING') events.push({ type: 'waitingForPlayers' });
}

function settle(s: RedDogState, kind: RedDogSpreadKind, events: EngineEvent[]): void {
  const [a, b] = s.spread!;
  const spread = redDogSpread(a, b);
  const seats: RedDogResult['seats'] = [];
  s.seats.forEach((p, seat) => {
    if (!p || p.status !== 'BET') return;
    let outcome: RedDogOutcome;
    let payout: number;
    if (kind === 'CONSECUTIVE') {
      outcome = 'PUSH';
      payout = p.bet;
    } else if (kind === 'PAIR') {
      const trips = s.third !== null && redDogValue(s.third) === redDogValue(a);
      outcome = trips ? 'TRIPS' : 'PUSH';
      payout = trips ? p.bet * (1 + s.config.tripsPays) : p.bet;
    } else {
      const won = redDogBetween(a, b, s.third!);
      outcome = won ? 'WIN' : 'LOSE';
      payout = won ? (p.bet + p.raise) * (1 + redDogOdds(spread)) : 0;
    }
    p.outcome = outcome;
    p.payout = payout;
    p.chips += payout;
    seats.push({ seat, bet: p.bet, raise: p.raise, outcome, payout, delta: payout - p.bet - p.raise });
  });
  s.lastResult = { handNo: s.handNo, spread: [a, b], third: s.third, kind, seats };
  s.handsPlayed++;
  s.phase = 'HAND_END';
  s.turn = -1;
  events.push({ type: 'handEnd', handNo: s.handNo, results: seats.map((r) => ({ seat: r.seat, delta: r.delta })) });
}

/** After a raise or stay: the next seat decides, otherwise the third card is drawn. */
function afterDecision(s: RedDogState, from: number): void {
  const next = nextDecider(s, from);
  if (next >= 0) {
    s.turn = next;
    return;
  }
  s.turn = -1;
  s.phase = 'DRAWING';
}

export const redDogEngine: GameEngine<RedDogConfig, RedDogState, RedDogAction, RedDogView, RedDogLegal> = {
  gameType: 'RED_DOG',
  kind: 'session',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('maxPlayers', c.maxPlayers, 1, 8);
    checkInt('minPlayers', c.minPlayers, 1, c.maxPlayers);
    checkInt('minBet', c.minBet, 1, 1e9);
    checkInt('maxBet', c.maxBet, c.minBet, 1e12);
    checkInt('tripsPays', c.tripsPays, 1, 50);
    checkInt('startDelayMs', c.startDelayMs, 0, 60000);
    checkInt('dealDelayMs', c.dealDelayMs, 0, 60000);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    return c;
  },

  seatLimits: (c) => ({ min: c.minPlayers, max: c.maxPlayers }),

  createState(config, seats, rng) {
    const s: RedDogState = {
      config,
      seats: Array<RedDogSeat | null>(config.maxPlayers).fill(null),
      phase: 'WAITING',
      handNo: 0,
      turn: -1,
      deck: shuffle(buildDeck(), rng),
      spread: null,
      third: null,
      lastResult: null,
      handsPlayed: 0,
    };
    for (const setup of seats) this.addSeat!(s, setup);
    return s;
  },

  addSeat(s, setup: SeatSetup) {
    assertEngine(setup.seat >= 0 && setup.seat < s.seats.length && !s.seats[setup.seat], 'SEAT_TAKEN', 'Seat unavailable');
    const chips = setup.chips ?? 0;
    assertEngine(Number.isInteger(chips) && chips >= 0, 'INVALID_AMOUNT', 'Invalid chips');
    // While antes are still being taken a later seat can join in; once the cards are coming they wait.
    const open = s.phase === 'WAITING' || s.phase === 'HAND_END' || (s.phase === 'BETTING' && chips >= s.config.minBet);
    s.seats[setup.seat] = { playerId: setup.playerId, chips, status: open ? 'WAITING' : 'SIT_OUT', bet: 0, raise: 0, decided: false, outcome: null, payout: 0 };
    return [{ type: 'playerJoined', seat: setup.seat, playerId: setup.playerId, chips }];
  },

  removeSeat(s, seat) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'Seat empty');
    let chips = p.chips;
    const wasTurn = s.turn === seat;
    if (s.phase === 'BETTING') {
      // Nothing is dealt yet: the ante comes back.
      chips += p.bet;
      s.seats[seat] = null;
      if (wasTurn) advanceBetting(s, seat);
    } else {
      // Leaving once the cards are coming forfeits the bets on the table.
      s.seats[seat] = null;
      if (s.phase === 'DECIDING' && wasTurn) afterDecision(s, seat);
    }
    return { chips, events: [{ type: 'playerLeft', seat }] };
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'sitOut') return { type: 'sitOut' };
    if (raw.type === 'stay') return { type: 'stay' };
    if ((raw.type === 'bet' || raw.type === 'raise') && typeof raw.amount === 'number' && Number.isInteger(raw.amount)) return { type: raw.type, amount: raw.amount };
    throw new EngineError('INVALID_ACTION', 'Unknown Red Dog action');
  },

  applyAction(s, seat, action) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'You are not at this table');
    const events: EngineEvent[] = [];
    if (action.type === 'raise' || action.type === 'stay') {
      assertEngine(s.phase === 'DECIDING', 'INVALID_PHASE', 'There is no spread to raise on');
      assertEngine(seat === s.turn, 'NOT_YOUR_TURN', 'Not your turn');
      if (action.type === 'raise') {
        const max = Math.min(p.bet, p.chips);
        assertEngine(action.amount >= 1 && action.amount <= max, 'INVALID_AMOUNT', `Raise between 1 and ${max}`);
        p.chips -= action.amount;
        p.raise = action.amount;
        events.push({ type: 'raise', seat, amount: action.amount });
      } else events.push({ type: 'stay', seat });
      p.decided = true;
      afterDecision(s, seat);
      return events;
    }
    assertEngine(s.phase === 'BETTING', 'INVALID_PHASE', 'Betting is closed');
    assertEngine(seat === s.turn, 'NOT_YOUR_TURN', 'Not your turn');
    if (action.type === 'sitOut') {
      p.status = 'SIT_OUT';
      events.push({ type: 'sitOut', seat });
      advanceBetting(s, seat);
      return events;
    }
    if (action.type !== 'bet') throw new EngineError('INVALID_ACTION', 'Unknown Red Dog action');
    const max = Math.min(s.config.maxBet, p.chips);
    assertEngine(action.amount >= s.config.minBet && action.amount <= max, 'INVALID_AMOUNT', `Bet between ${s.config.minBet} and ${max}`);
    p.chips -= action.amount;
    p.bet = action.amount;
    p.status = 'BET';
    events.push({ type: 'bet', seat, amount: action.amount });
    advanceBetting(s, seat);
    return events;
  },

  getActors(s) {
    return (s.phase === 'BETTING' || s.phase === 'DECIDING') && s.turn >= 0 && s.seats[s.turn] ? [s.turn] : [];
  },

  getLegalActions(s, seat) {
    const p = s.seats[seat];
    if (!p || seat !== s.turn) return { kind: 'none' };
    if (s.phase === 'BETTING') return { kind: 'bet', min: s.config.minBet, max: Math.min(s.config.maxBet, p.chips), canSitOut: true };
    if (s.phase === 'DECIDING' && s.spread) return { kind: 'decide', maxRaise: Math.min(p.bet, p.chips), spread: redDogSpread(s.spread[0], s.spread[1]) };
    return { kind: 'none' };
  },

  getTimeoutAction(s) {
    return s.phase === 'DECIDING' ? { type: 'stay' } : { type: 'sitOut' };
  },

  getView(s, seat) {
    return {
      gameType: 'RED_DOG',
      config: s.config,
      seat,
      phase: s.phase,
      handNo: s.handNo,
      turn: s.turn,
      spread: s.spread ? [...s.spread] : null,
      third: s.third,
      spreadSize: s.spread ? redDogSpread(s.spread[0], s.spread[1]) : null,
      deckCount: s.deck.length,
      players: s.seats.map((p, i) => (p ? { seat: i, playerId: p.playerId, chips: p.chips, status: p.status, bet: p.bet, raise: p.raise, decided: p.decided, outcome: p.outcome, payout: p.payout } : null)),
      lastResult: s.lastResult ? structuredClone(s.lastResult) : null,
    };
  },

  getSystemStep(s) {
    if (s.phase === 'DEALING' || s.phase === 'DRAWING') return { kind: 'deal', delayMs: s.config.dealDelayMs };
    if (s.phase === 'HAND_END') return { kind: 'nextHand', delayMs: s.config.handEndDelayMs };
    if (s.phase === 'WAITING' && canStart(s)) return { kind: 'startHand', delayMs: s.config.startDelayMs };
    return null;
  },

  runSystemStep(s, rng) {
    const events: EngineEvent[] = [];
    if (s.phase === 'DEALING') {
      const a = s.deck.pop()!;
      const b = s.deck.pop()!;
      s.spread = [a, b];
      events.push({ type: 'cardDealt', to: 'SPREAD', card: a }, { type: 'cardDealt', to: 'SPREAD', card: b });
      const kind = redDogKind(a, b);
      if (kind === 'CONSECUTIVE') settle(s, kind, events);
      else if (kind === 'PAIR') {
        // Equal ranks: one more card decides between three of a kind and a push.
        s.third = s.deck.pop()!;
        events.push({ type: 'cardDealt', to: 'SPREAD', card: s.third });
        settle(s, kind, events);
      } else {
        const first = nextDecider(s, -1);
        if (first >= 0) {
          s.phase = 'DECIDING';
          s.turn = first;
        } else {
          s.phase = 'DRAWING';
          s.turn = -1;
        }
      }
      return events;
    }
    if (s.phase === 'DRAWING') {
      s.third = s.deck.pop()!;
      events.push({ type: 'cardDealt', to: 'SPREAD', card: s.third });
      settle(s, 'SPREAD', events);
      return events;
    }
    if (s.phase === 'HAND_END' || s.phase === 'WAITING') {
      s.phase = 'WAITING';
      if (canStart(s)) startHand(s, rng, events);
      else events.push({ type: 'waitingForPlayers' });
    }
    return events;
  },

  isFinished: () => false,
  isHandInProgress: (s) => s.phase === 'DEALING' || s.phase === 'DECIDING' || s.phase === 'DRAWING',
  getChips: (s) => Object.fromEntries(seatsWhere(s, () => true).map((i) => [i, s.seats[i]!.chips])),
  getStandings: (s) => standingsFromScores(seatsWhere(s, () => true).map((seat) => ({ seat, score: s.seats[seat]!.chips }))),
  getRoundsCompleted: (s) => s.handsPlayed,
};
